import { projectStatusSchema, type ProjectSummary } from '@pm-tool/shared';
import { z } from 'zod';

/** Organization filter value for projects without a customer organization. */
export const INTERNAL_PROJECT = 'internal';

/**
 * URL state of the project overview (PROJ-05): archive switch, view and filters. Filtering runs
 * in the browser over the visible projects; invalid values are dropped one by one.
 */
const fieldSchemas = {
  archived: z.boolean(),
  view: z.literal('list'),
  q: z.string().trim().min(1).max(200),
  status: z.array(projectStatusSchema).min(1),
  org: z.array(z.union([z.uuid(), z.literal(INTERNAL_PROJECT)])).min(1),
  lead: z.array(z.uuid()).min(1),
};

export type ProjectListSearch = {
  [K in keyof typeof fieldSchemas]?: z.infer<(typeof fieldSchemas)[K]>;
};

export function parseProjectListSearch(raw: Record<string, unknown>): ProjectListSearch {
  const result: Record<string, unknown> = {};
  for (const [key, schema] of Object.entries(fieldSchemas)) {
    const value = raw[key];
    if (value === undefined || value === '') continue;
    const parsed = (schema as z.ZodType).safeParse(value); // each entry is a Zod schema
    if (parsed.success) result[key] = parsed.data;
  }
  return result;
}

/** Applies a change; `undefined` removes the value. */
export function patchProjectListSearch(
  search: ProjectListSearch,
  patch: { [K in keyof ProjectListSearch]?: ProjectListSearch[K] | undefined },
): ProjectListSearch {
  return Object.fromEntries(
    Object.entries({ ...search, ...patch }).filter(([, v]) => v !== undefined),
  );
}

export function hasProjectFilters(search: ProjectListSearch): boolean {
  return (
    search.q !== undefined ||
    search.status !== undefined ||
    search.org !== undefined ||
    search.lead !== undefined
  );
}

export function filterProjects(
  projects: readonly ProjectSummary[],
  search: ProjectListSearch,
): ProjectSummary[] {
  const q = search.q?.toLocaleLowerCase('de-DE');
  return projects.filter(
    (p) =>
      (q === undefined ||
        p.name.toLocaleLowerCase('de-DE').includes(q) ||
        p.key.toLocaleLowerCase('de-DE').includes(q)) &&
      (search.status === undefined || search.status.includes(p.status)) &&
      (search.org === undefined || search.org.includes(p.organization?.id ?? INTERNAL_PROJECT)) &&
      (search.lead === undefined ||
        (p.lead !== null && !p.lead.hidden && search.lead.includes(p.lead.id))),
  );
}
